/* ScheduleVeilRenderer.js */

"use strict";
const _ = require("lodash");
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const ScheduleComponent = require("@gluenetworks/schedule_component");
const NotificationsComponent = require("@gluenetworks/notification_component");

const InputRadioControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single.InputRadio;

class ScheduleVeilRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("schedule-veil-gl"); //TODO Setup the root Element
        this._rootElem.component = this._model;
        if (this.model.darkTheme) {
            this._rootElem.classList.add('dark');
        }

        this._confirmButton = new ButtonControl({
            label: "Confirm",
            darkTheme: this.model.darkTheme
        });

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: this.model.darkTheme
        });

        let allowRepeat = _.isUndefined(this._model._scheduleRepeat) ? true : this._model._scheduleRepeat;
        this._scheduleComponent = new ScheduleComponent(undefined, {
            allowOne: true,
            allowRepeat: allowRepeat,
            pollOnly: false,
            availOnly: false,
            firstTime: this.model.firstTime
        });
        this._scheduleComponent.theme = "dark";

        this._notificationsComponent = new NotificationsComponent(this.model.logger, this.model.userSession, {darkTheme: this.model.darkTheme, title: "Notify on Completion", workType: this.model.workType});

        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = this._model.titleText;

        this._buttonContainer = document.createElement("button-container");

        this._msgComponent = document.createElement("msg");

        this._reportTypeSel = new InputRadioControl({
            labelShown: true,
            horizontal: true,
            options: [
                {
                    label: "CSV",
                    value: "csv"
                },
                {
                    label: "PDF",
                    value: "pdf"
                },
                {
                    label: "JSON",
                    value: "json"
                }
            ],
            value: "csv",
            label: "Report format"
        });

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._confirmButton.on(
            "UserClick",
            (this._onConfirmButtonClick = this._onConfirmButtonClick.bind(this))
        );
        this._cancelButton.on(
            "UserClick",
            (this._onCancelButtonClick = this._onCancelButtonClick.bind(this))
        );
        this._scheduleComponent.renderer.rootElement.addEventListener(
            "click",
            (this._onSchedClick = this._onSchedClick.bind(this)),
            true
        );
        this._notificationsComponent.on("ValidationStateChanged", this._onValidationStateChanged = this._onValidationStateChanged.bind(this));
        if(this._model._soln === "DE") {
            this._reportTypeSel.on("UserValue", this._onReportTypeSelChange = this._onReportTypeSelChange.bind(this));
        }
    }

    _unregisterListeners() {
        this._confirmButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
        this._scheduleComponent.renderer.rootElement.removeEventListener(
            "click",
            this._onSchedClick
        );
        this._notificationsComponent.removeListener("ValidationStateChanged", this._onValidationStateChanged);
        if(this._model._soln === "DE") {
            this._reportTypeSel.removeAllListeners("UserValue");
        }
    }

    _onConfirmButtonClick() {
        let validationErrors = this._scheduleComponent.validateSchedule();
        if (_.isEmpty(validationErrors)) {
            this._callSaveSchedule();
        } else {
            let msg = "";
            validationErrors.forEach((validationMsg) => {
                msg += `<p>${validationMsg}</p>`;
            });
            this._msgComponent.innerHTML = msg;
        }
    }

    _onCancelButtonClick() {
        this._callCancelSchedule();
    }

    _onSchedClick() {
        this._msgComponent.innerHTML = "";
    }

    _checkForImpliedConfirm(event) {
        if (event.keyCode === 13) {
            this._callStartCapture();
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        this._unregisterListeners();
        let promArr = [
            this._scheduleComponent.shutdown(),
            this._notificationsComponent.shutdown(),
            this._confirmButton.shutdown(),
            this._cancelButton.shutdown()
        ];

        if(this._model._soln === "DE") {
            promArr.push(this._reportTypeSel.shutdown());
        }

        return Promise.all(promArr).then(
            () => {
                this._confirmButton = undefined;
                this._cancel = undefined;
                this._buttonContainer.remove();
                this._buttonContainer = undefined;
                this._titleComponent.remove();
                this._titleComponent = undefined;
                this._notificationsComponent = undefined;
                this._reportTypeSel = undefined;
                this._msgComponent = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._model = undefined;
            },
            (err) => {
                this._model.logger.warn(err, "ScheduleVeilRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve) => {
            if (!this._rootElem.hasChildNodes()) {
                let renderPromises = [
                    this._scheduleComponent.renderer.render(),
                    this._notificationsComponent.renderer.render()
                ];
                return Promise.all(renderPromises).then(() => {
                    this._rootElem.appendChild(this._titleComponent);
                    this._rootElem.appendChild(this._scheduleComponent.renderer.rootElement);
                    this._rootElem.appendChild(this._notificationsComponent.renderer.rootElement);
                    if(this._model._soln === "DE") {
                        this._rootElem.appendChild(this._createControlWithLabel(this._reportTypeSel, "Report format"));
                    }
                    this._rootElem.appendChild(this._buttonContainer);
                    this._rootElem.appendChild(this._msgComponent);

                    this._buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
                    this._buttonContainer.appendChild(this._confirmButton.renderer.rootElement);
                    return Promise.resolve();
                });
            }
            resolve();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/
    _enableComfirmButtons(enable) {
        this._confirmButton.disabled = !enable;
    }
    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onValidationStateChanged(isValid) {
        this._enableComfirmButtons(isValid);
    }

    _onReportTypeSelChange(val) {
        this._model.reportFormat = val;
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _callSaveSchedule() {
        this._model.saveSchedule({
            schedule: this._scheduleComponent.getSchedule(),
            workInput: this._notificationsComponent.getNotificationsWorkInputObject()
        });
    }

    _callCancelSchedule() {
        this._model.cancelSchedule({ userRejected: true });
    }

    _createControlWithLabel(control, label = "") {
        const elem = document.createElement("div");
        elem.classList.add("control-inp");
        elem.classList.add(label.toLowerCase().replace(" ","_").replace(/\W/g, ""), "label");
        const labelEl = document.createElement("label");
        labelEl.innerText = label;
        elem.appendChild(labelEl);
        elem.appendChild(control.renderer.rootElement);
        return elem;

    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = ScheduleVeilRenderer;
